import { InvestmentPathDetail } from '../interfaces/DTO/mislaka_xml_models';
import { fundService } from './fundService';
import { formatCurrency } from '../utils/namesFormater';

export const IRA_SUB_SPECIALIZATION = 'בניהול אישי IRA';

export const YIELD_FIELDS = [
  { key: 'year_to_date_yield' as const,              label: 'YTD' },
  { key: 'cumulative_6m_yield' as const,             label: '6M'  },
  { key: 'cumulative_12m_yield' as const,            label: '1Y'  },
  { key: 'avg_annual_yield_trailing_3yrs' as const,  label: '3Y'  },
  { key: 'avg_annual_yield_trailing_5yrs' as const,  label: '5Y'  },
] as const;

export type YieldFieldKey = typeof YIELD_FIELDS[number]['key'];

export interface ExcludedPathEntry {
  fundName?: string;
  pathCode?: number;
  amount: number;
  reason: 'IRA' | 'missing_data';
  policyNumber?: string;
}

export interface YieldFieldResult {
  average: number;
  excluded: ExcludedPathEntry[];
}

export interface CumulativeYields {
  trailing3y: number | null;
  trailing5y: number | null;
}

export type WeightedYieldResult = {
  [K in YieldFieldKey]: YieldFieldResult;
} & { totalAmount: number; cumulativeYields: CumulativeYields };

export function computeWeightedYieldsFromPaths(
  allPaths: { path: InvestmentPathDetail; policyNumber?: string }[]
): WeightedYieldResult {
  const acc: { [K in YieldFieldKey]: { weightedSum: number; validAmount: number; excluded: ExcludedPathEntry[] } } = {
    year_to_date_yield:             { weightedSum: 0, validAmount: 0, excluded: [] },
    cumulative_6m_yield:            { weightedSum: 0, validAmount: 0, excluded: [] },
    cumulative_12m_yield:           { weightedSum: 0, validAmount: 0, excluded: [] },
    avg_annual_yield_trailing_3yrs: { weightedSum: 0, validAmount: 0, excluded: [] },
    avg_annual_yield_trailing_5yrs: { weightedSum: 0, validAmount: 0, excluded: [] },
  };

  for (const { path, policyNumber } of allPaths) {
    const fund = path.path_code !== undefined ? fundService.getFundFromCacheById(path.path_code) : undefined;
    const amount = path.accumulated_amount ?? 0;
    if (amount <= 0) continue;

    for (const field of YIELD_FIELDS) {
      const k = field.key;
      if (!fund) {
        acc[k].excluded.push({ pathCode: path.path_code, fundName: path.path_name, amount, reason: 'missing_data', policyNumber });
        continue;
      }
      if (fund.sub_specialization === IRA_SUB_SPECIALIZATION) {
        acc[k].excluded.push({ pathCode: path.path_code, fundName: fund.fund_name ?? path.path_name, amount, reason: 'IRA', policyNumber });
        continue;
      }
      if (fund[k] === undefined || fund[k] === null) {
        acc[k].excluded.push({ pathCode: path.path_code, fundName: fund.fund_name ?? path.path_name, amount, reason: 'missing_data', policyNumber });
        continue;
      }
      acc[k].weightedSum += amount * fund[k];
      acc[k].validAmount += amount;
    }
  }

  const totalAmount = allPaths.reduce((sum, { path }) => sum + (path.accumulated_amount ?? 0), 0);

  let cum3ySum = 0, cum3yValid = 0, cum5ySum = 0, cum5yValid = 0;
  for (const { path } of allPaths) {
    const amount = path.accumulated_amount ?? 0;
    if (amount <= 0) continue;
    const fund = path.path_code !== undefined ? fundService.getFundFromCacheById(path.path_code) : undefined;
    if (!fund || fund.sub_specialization === IRA_SUB_SPECIALIZATION) continue;
    if (fund.yield_trailing_3_yrs != null) { cum3ySum += fund.yield_trailing_3_yrs * amount; cum3yValid += amount; }
    if (fund.yield_trailing_5_yrs != null) { cum5ySum += fund.yield_trailing_5_yrs * amount; cum5yValid += amount; }
  }

  return {
    year_to_date_yield:             { average: acc.year_to_date_yield.validAmount > 0             ? acc.year_to_date_yield.weightedSum / acc.year_to_date_yield.validAmount : 0,             excluded: acc.year_to_date_yield.excluded },
    cumulative_6m_yield:            { average: acc.cumulative_6m_yield.validAmount > 0            ? acc.cumulative_6m_yield.weightedSum / acc.cumulative_6m_yield.validAmount : 0,            excluded: acc.cumulative_6m_yield.excluded },
    cumulative_12m_yield:           { average: acc.cumulative_12m_yield.validAmount > 0           ? acc.cumulative_12m_yield.weightedSum / acc.cumulative_12m_yield.validAmount : 0,           excluded: acc.cumulative_12m_yield.excluded },
    avg_annual_yield_trailing_3yrs: { average: acc.avg_annual_yield_trailing_3yrs.validAmount > 0 ? acc.avg_annual_yield_trailing_3yrs.weightedSum / acc.avg_annual_yield_trailing_3yrs.validAmount : 0, excluded: acc.avg_annual_yield_trailing_3yrs.excluded },
    avg_annual_yield_trailing_5yrs: { average: acc.avg_annual_yield_trailing_5yrs.validAmount > 0 ? acc.avg_annual_yield_trailing_5yrs.weightedSum / acc.avg_annual_yield_trailing_5yrs.validAmount : 0, excluded: acc.avg_annual_yield_trailing_5yrs.excluded },
    totalAmount,
    cumulativeYields: {
      trailing3y: cum3yValid > 0 ? cum3ySum / cum3yValid : null,
      trailing5y: cum5yValid > 0 ? cum5ySum / cum5yValid : null,
    },
  };
}

export function buildExcludedTooltip(items: ExcludedPathEntry[]): string {
  if (items.length === 0) return '';
  const iraItems = items.filter(e => e.reason === 'IRA');
  const missingItems = items.filter(e => e.reason === 'missing_data');
  const totalAmount = items.reduce((sum, e) => sum + e.amount, 0);

  const lines: string[] = [`סכום כולל מוחרג: ${formatCurrency(totalAmount)}\n`];
  if (iraItems.length > 0) {
    lines.push('מסלולי IRA (בניהול אישי) - מוחרגים מהחישוב:');
    iraItems.forEach(e => lines.push(`  • ${e.fundName ?? `קוד ${e.pathCode}`}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ''} — ${formatCurrency(e.amount)}`));
  }
  if (missingItems.length > 0) {
    if (iraItems.length > 0) lines.push('');
    lines.push('מסלולים ללא נתוני תשואה:');
    missingItems.forEach(e => lines.push(`  • ${e.fundName ?? `קוד ${e.pathCode}`}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ''} — ${formatCurrency(e.amount)}`));
  }
  return lines.join('\n');
}
